import { lazy, useCallback, useEffect, useState, Suspense } from 'react';
import { COGNITO_CONFIG, REFRESH_TOKEN, TOKEN } from 'constants/localStorageConstants';
import {
	amplifyErrorSelector,
	refreshTokenSelector,
	setAmplifyError,
	setToken,
	tokenSelector
} from 'reducers/userAuthSlice';
import { addSnackbarAlerts, resetSnackbarAlerts } from 'reducers/globalSlice';
import { reduxStore } from 'services/configureStore';
import { handleAwsCognito } from 'services/aws-cognito';
import { COGNITO_REDIRECT_URI } from 'constants/environmentVariables';
import {
	checkRedirectURIAllowed,
	createAdminPageRedirect,
	createMobileRedirectUrl,
	getDefaultDomainConfigObj,
	getDomainFromInput,
	mapDomainToValues,
	parseLoginError
} from 'components/authentication/authenticationUtils';
import { amplifySignIn } from 'actions/authentication';
import {
	Button,
	IconButton,
	InputAdornment,
	LinearProgress,
	TextField,
	Link,
	Grid,
	Typography,
	Box,
	useTheme
} from '@mui/material';
import { authenticationPagesStyles } from 'constants/styles';
import AirControl from 'assets/images/air_control_white.png';
import AloftLogo from 'assets/images/aloft_logo_white.png';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	getQueryParamsFromLocation,
	setQueryParamsFromObject,
	stringifyQueryObj
} from 'utils/urlHelpers';
import queryString from 'query-string';
import { checkTokenExpired } from 'utils/tokenUtils';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const AuthenticationFooter = lazy(() => import('components/authentication/AuthenticationFooter'));
const LanguageSwitcherWrap = lazy(() => import('components/elements/LanguageSwitcherWrap'));

const SignIn = () => {
	const { t } = useTranslation();
	const theme = useTheme();
	const styles = {
		...authenticationPagesStyles(theme)
	};
	const [domain, setDomain] = useState(null);
	const [domainValues, setDomainValues] = useState(null);
	const [showLoginButton, setShowLoginButton] = useState(false);
	const [showNextButton, setShowNextButton] = useState(true);
	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [showLoader, setShowLoader] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();
	const amplifyError = useSelector(amplifyErrorSelector);
	const queryParameters = queryString.parse(window.location.search);
	const dispatch = useDispatch();
	const token = useSelector(tokenSelector);
	const refreshToken = useSelector(refreshTokenSelector);

	const parseRedirectURI = () => {
		const parsedObject = getQueryParamsFromLocation(location);
		return parsedObject.success_redirect_uri;
	};
	const successRedirectURI = parseRedirectURI();

	const createCognitoConfig = useCallback(
		domainValues => {
			const { client_id, pool, host } = domainValues;
			return {
				clientId: client_id,
				userPoolId: pool,
				redirectURI: COGNITO_REDIRECT_URI,
				successRedirectURI,
				host
			};
		},
		[successRedirectURI]
	);

	const signIn = async currentDomainValues => {
		dispatch(resetSnackbarAlerts([]));
		const cognitoConfig = createCognitoConfig(currentDomainValues);
		localStorage.setItem(COGNITO_CONFIG, JSON.stringify(cognitoConfig));
		const lowercaseUsername = username.toLowerCase();
		const cognitoUser = await dispatch(
			amplifySignIn({
				username: lowercaseUsername,
				password,
				configObj: cognitoConfig,
				navigate,
				queryParams: queryParameters
			})
		);
		if (!cognitoUser) {
			return;
		}
		const idToken = cognitoUser.signInUserSession.idToken.jwtToken;
		const refreshToken = cognitoUser.signInUserSession.refreshToken.token;
		const accessToken = cognitoUser.signInUserSession.accessToken.jwtToken;

		if (successRedirectURI && checkRedirectURIAllowed(successRedirectURI)) {
			//Mobile flow
			const mobileRedirect = createMobileRedirectUrl({
				host: cognitoConfig.host,
				clientId: cognitoConfig.clientId,
				successRedirectURI,
				refreshToken,
				idToken,
				accessToken
			});

			//Send Mobile all of the info in the url
			window.location.assign(mobileRedirect);
		} else {
			//Web flow
			localStorage.setItem(TOKEN, idToken);
			localStorage.setItem(REFRESH_TOKEN, refreshToken);
			reduxStore.dispatch(setToken(idToken));
			window.location.assign(cognitoConfig.redirectURI);
		}
	};

	const handleUsernameChange = e => {
		let updatedQP = {
			...queryParameters
		};
		delete updatedQP.signUpSuccess;
		setQueryParamsFromObject(updatedQP, navigate);
		const trimmedValue = e.target.value.trim();
		setUsername(trimmedValue);
		//Pass lowercase value to domain, but still display what the user actually typed in
		const lowercaseValue = trimmedValue.toLowerCase();
		const domain = getDomainFromInput(lowercaseValue);
		setDomain(domain);
	};

	const handlePasswordChange = e => {
		const value = e.target.value;
		setPassword(value);
	};

	const handleNext = () => {
		dispatch(resetSnackbarAlerts([]));
		const newDomainValues = mapDomainToValues(domain);
		if (newDomainValues) {
			if (
				(successRedirectURI && checkRedirectURIAllowed(successRedirectURI)) ||
				!successRedirectURI
			) {
				const cognitoConfig = createCognitoConfig(newDomainValues);
				setShowLoader(true);
				handleRedirectToSSO(cognitoConfig);
			}
		} else {
			const defaultDomainValues = getDefaultDomainConfigObj();
			setDomainValues(defaultDomainValues);
			setShowLoginButton(true);
			setShowNextButton(false);
		}
	};

	const handleRedirectToSSO = config => {
		localStorage.setItem(COGNITO_CONFIG, JSON.stringify(config));
		handleAwsCognito(config);
	};

	const handleForgotPassword = async () => {
		let resetPasswordURL = `/reset-password`;
		if (username) {
			const lowercaseUsername = username.toLowerCase();
			resetPasswordURL = `/reset-password?email=${lowercaseUsername}`;
			if (successRedirectURI && checkRedirectURIAllowed(successRedirectURI)) {
				resetPasswordURL = `/reset-password?success_redirect_uri=${successRedirectURI}&email=${lowercaseUsername}`;
			}
		} else {
			if (successRedirectURI && checkRedirectURIAllowed(successRedirectURI)) {
				resetPasswordURL = `/reset-password?success_redirect_uri=${successRedirectURI}`;
			}
		}
		navigate(resetPasswordURL);
	};

	const handleUsernameKeyDown = e => {
		// Trigger next on Enter
		if (e.keyCode === 13) {
			handleNext();
		}
	};

	const handlePasswordKeyDown = e => {
		// Trigger login on Enter
		if (e.keyCode === 13) {
			handleLogin();
		}
	};

	const handleLogin = () => {
		setShowLoader(true);
		signIn(domainValues);
	};

	const handleClickSignUp = () => {
		//Pass along any query params that were sent by the mobile app
		navigate({
			pathname: '/signup',
			search: stringifyQueryObj(queryParameters)
		});
	};

	const handleClickLogin = () => {
		navigate(`/login`);
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	useEffect(() => {
		const handleLoginWithSSO = currentDomainValues => {
			const cognitoConfig = createCognitoConfig(currentDomainValues);
			handleRedirectToSSO(cognitoConfig);
		};

		const handleNextWithParameter = domain => {
			const newDomainValues = mapDomainToValues(domain);
			if (newDomainValues) {
				if (
					(successRedirectURI && checkRedirectURIAllowed(successRedirectURI)) ||
					!successRedirectURI
				) {
					setShowLoader(true);
					handleLoginWithSSO(newDomainValues);
				}
			} else {
				const defaultDomainValues = getDefaultDomainConfigObj();
				setDomainValues(defaultDomainValues);
				setShowLoginButton(true);
			}
		};

		if (!token) {
			//Populate email and go to next step automatically when coming from mobile with email in query param
			const queryParameterFromMobile = getQueryParamsFromLocation(location);
			const { email } = queryParameterFromMobile;
			//Only set username from query param if it hasn't already been set
			if (email && !username) {
				setShowLoginButton(false);
				setShowNextButton(false);
				setUsername(email);
				const domain = getDomainFromInput(email);
				handleNextWithParameter(domain);
			}
		}
	}, [location, createCognitoConfig, token, successRedirectURI, username]);

	useEffect(() => {
		if (amplifyError) {
			setDomain(null);
			setDomainValues(null);
			setShowLoginButton(false);
			setShowNextButton(true);
			setPassword('');
			setShowLoader(false);
			const queryParameterFromMobile = getQueryParamsFromLocation(location);
			const { email } = queryParameterFromMobile;
			if (email) {
				setUsername(email);
			} else {
				setUsername('');
			}
			dispatch(setAmplifyError(false)); //Remove amplify error after it has been shown and state has been reset
		}
	}, [amplifyError, location, dispatch]);

	useEffect(() => {
		//Error from cognito
		if (queryParameters.error) {
			dispatch(
				addSnackbarAlerts([
					{
						type: 'error',
						message: parseLoginError(queryParameters.error)
					}
				])
			);
		}
	}, [queryParameters.error, dispatch]);

	if (token) {
		//User is already logged in
		const tokenExpired = checkTokenExpired(token);
		if (successRedirectURI && checkRedirectURIAllowed(successRedirectURI)) {
			//Admin page login flow
			if (tokenExpired) {
				// Cause token to refresh by going to /account-picker, which triggers App component to call /whoami
				// If token is expired on API call it will be refreshed
				// Query param success_redirect_uri tells App to redirect to admin page
				navigate(`/account-picker?success_redirect_uri=${successRedirectURI}`);
			} else {
				// Go to provided success_redirect_uri and pass token
				const cognitoConfig = JSON.parse(localStorage.getItem(COGNITO_CONFIG));
				const redirect = createAdminPageRedirect({
					host: cognitoConfig.host,
					clientId: cognitoConfig.clientId,
					successRedirectURI,
					idToken: token,
					refreshToken
				});
				window.location.assign(redirect);
			}
		} else {
			//Go to account selector
			return <Navigate to='/account-picker' />;
		}
		return null;
	}

	return (
		<Suspense fallback={null}>
			<LanguageSwitcherWrap>
				<Box sx={styles.main}>
					<Box sx={styles.headerContainer}>
						<Box
							component={'img'}
							src={AloftLogo}
							alt=''
							onClick={handleClickLogin}
							sx={styles.logo}
						/>
						<Link
							href='https://www.aloft.ai/support/'
							target='_blank'
							rel='noopener noreferrer'
							underline='none'
						>
							<Button
								sx={{ ...styles.outlineButton, ...styles.topSupportButton }}
								variant='outlined'
							>
								{t('Support')}
							</Button>
						</Link>
					</Box>
					<Grid container sx={styles.mainContainer}>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.leftContainerText}>
							<Box component={'img'} src={AirControl} alt='' sx={styles.airControlLogo} />
							<Typography sx={styles.authCopy}>
								{t('Log into Air Control to manage your drone data and customize your experience')}.{' '}
								{t('Sign in with the same credentials you use to sign into the mobile apps')}.
							</Typography>
							<Typography sx={styles.authCopy}>
								{t('Check airspace, customize workflows, apply for LAANC authorizations')}.
							</Typography>
							<Typography sx={styles.authCopy}>
								{t("If you don't have an account")} <br />
								{t('You can')}{' '}
								<Box component={'span'} sx={styles.actionLink} onClick={handleClickSignUp}>
									{t('Sign up here!')}
								</Box>
							</Typography>
						</Grid>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.rightContainer}>
							<Box elevation={0} sx={styles.outlineContainer}>
								<Box sx={styles.formTitle}>
									{t('Sign In')}
									{showLoader && <LinearProgress />}
								</Box>

								<>
									<TextField
										variant='outlined'
										onChange={handleUsernameChange}
										id='username'
										name='username'
										sx={styles.textFieldNoMargin}
										placeholder={t('Email')}
										value={username}
										onKeyDown={handleUsernameKeyDown}
										autoFocus
									/>
									{showLoginButton && (
										<TextField
											inputProps={{ type: showPassword ? 'text' : 'password' }}
											variant='outlined'
											onChange={handlePasswordChange}
											id='password'
											name='password'
											placeholder={t('Password')}
											sx={styles.textField}
											onKeyDown={handlePasswordKeyDown}
											autoFocus={showLoginButton}
											InputProps={{
												endAdornment: (
													<InputAdornment position='end'>
														<IconButton onClick={handleClickShowPassword}>
															{showPassword ? <Visibility /> : <VisibilityOff />}
														</IconButton>
													</InputAdornment>
												)
											}}
										/>
									)}
									<Button sx={styles.forgotPassword} onClick={handleForgotPassword} color='primary'>
										{t('Forgot password?')}
									</Button>
									{showLoginButton && (
										<Button
											sx={styles.button}
											variant='contained'
											onClick={handleLogin}
											id='login'
											color='primary'
										>
											{t('Submit')}
										</Button>
									)}
									{showNextButton && (
										<Button
											disabled={!username}
											sx={styles.button}
											variant='contained'
											color='primary'
											onClick={handleNext}
											id='next'
										>
											{t('Next')}
										</Button>
									)}
								</>
							</Box>
							<Box sx={styles.belowSignIn}>
								<Typography sx={{ textAlign: 'center' }}>
									{t(
										'Log into Air Control to manage your drone data and customize your experience'
									)}
									. {t('Sign in with the same credentials you use to sign into the mobile apps')}.{' '}
									{t('Check airspace, customize workflows, apply for LAANC authorizations')}.
								</Typography>
								<Typography sx={{ textAlign: 'center' }}>
									{t("If you don't have an account you can")}{' '}
									<Box component={'span'} sx={styles.actionLink} onClick={handleClickSignUp}>
										{t('Sign up here!')}
									</Box>
								</Typography>
								<Link
									href='https://www.aloft.ai/support/'
									target='_blank'
									rel='noopener noreferrer'
									underline='none'
									sx={styles.bottomSupportButton}
								>
									<Button sx={styles.outlineButton} variant='outlined' fullWidth>
										{t('Support')}
									</Button>
								</Link>
							</Box>
						</Grid>
					</Grid>
					<Suspense fallback={null}>
						<AuthenticationFooter />
					</Suspense>
				</Box>
			</LanguageSwitcherWrap>
		</Suspense>
	);
};

export default SignIn;
